import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = getCategoryBySlug(slug || "");
  const categoryArticles = activeCategory
    ? getArticlesByCategory(activeCategory.id)
    : [];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Category Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              ...(activeCategory
                ? [{ label: activeCategory.name }]
                : []),
            ]}
          />
          <div className="px-4 md:px-6 lg:px-10 pb-10">
            <div className="max-w-7xl mx-auto">
              {activeCategory && (
                <div className="max-w-2xl">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-50 rounded-full mb-4">
                    <i className={`${activeCategory.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {activeCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {activeCategory.description}
                  </p>
                </div>
              )}
              {!activeCategory && (
                <div className="text-center py-10">
                  <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">
                    分类未找到
                  </h1>
                  <p className="text-sm text-foreground-500">
                    请检查链接是否正确，或返回首页浏览其他内容。
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 lg:px-10 py-6 bg-background-50 border-b border-background-200/70 overflow-x-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 whitespace-nowrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer ${
                    slug === cat.slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="px-4 md:px-6 lg:px-10 py-10">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <p className="text-sm text-foreground-500 mb-6">
                  共 {categoryArticles.length} 篇文章
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : (
              activeCategory && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                    <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    该分类下暂无文章
                  </p>
                </div>
              )
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="px-4 md:px-6 lg:px-10 py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">
              探索更多分类
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-4 transition-all duration-300 hover:border-primary-200/60 cursor-pointer"
                  >
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-50 rounded-full">
                      <i className={`${cat.icon} text-lg text-primary-500`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 whitespace-nowrap">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1">
                        {cat.description}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": activeCategory
              ? `${activeCategory.name} - 女王调教指南`
              : "分类页面",
            "description": activeCategory?.description || "",
            "url": window.location.href,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": window.location.origin,
                },
                ...(activeCategory
                  ? [
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": activeCategory.name,
                      },
                    ]
                  : []),
              ],
            },
          }),
        }}
      />
    </>
  );
}